/**
 * Shared guards for global/region keyboard shortcuts (e.g. tree Delete).
 *
 * A destructive shortcut like Delete must NOT fire while a dialog/modal is open
 * or while the user is typing in a field — otherwise pressing Delete to confirm
 * another action, or to edit text, can silently delete the selected tree item
 * (T1.7 — data-loss risk).
 */

/**
 * True when a MUI dialog/modal/backdrop or any role="dialog" element is mounted
 * and visible.
 * @param {Document} [doc=document]
 * @returns {boolean}
 */
export function isModalOpen(doc = document) {
  if (!doc || typeof doc.querySelector !== 'function') return false;
  return Boolean(
    doc.querySelector(
      '.MuiBackdrop-root:not([aria-hidden="true"]), .MuiDialog-root, .MuiModal-root'
    ) || doc.querySelector('[role="dialog"]')
  );
}

/**
 * True when the focused element accepts text input (input/textarea/
 * contenteditable/role=textbox).
 * @param {Document} [doc=document]
 * @returns {boolean}
 */
export function isTextInputFocused(doc = document) {
  const el = doc && doc.activeElement;
  if (!el) return false;
  return (
    el.tagName === 'INPUT' ||
    el.tagName === 'TEXTAREA' ||
    el.contentEditable === 'true' ||
    el.isContentEditable === true ||
    (typeof el.getAttribute === 'function' && el.getAttribute('role') === 'textbox')
  );
}

/**
 * True when a tree keyboard shortcut should be suppressed because a modal is
 * open or a text field is focused.
 * @param {Document} [doc=document]
 * @returns {boolean}
 */
export function shouldSuppressTreeShortcut(doc = document) {
  return isModalOpen(doc) || isTextInputFocused(doc);
}
